/**
 * Thin fetch wrapper. Always sends cookies (credentials: 'include')
 * because authentication is session-cookie based, and parses JSON
 * error responses into a consistent ApiError.
 */

export interface ApiErrorBody {
  error: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(init.headers ?? {}),
    },
    ...init,
  });

  if (!res.ok) {
    let body: Partial<ApiErrorBody> = {};
    try {
      body = await res.json();
    } catch {
      // non-JSON body (e.g. nginx 413/502 HTML), leave fields empty
    }
    // statusText is empty over HTTP/2, so always fall back to something
    // legible rather than an empty message ("No error message" in Sentry).
    const fallback =
      res.status === 413
        ? 'That file is too large to upload.'
        : res.statusText || `Request failed (${res.status})`;
    throw new ApiError(res.status, body.error ?? 'UnknownError', body.message || fallback);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}
